import { useCallback, type FC } from "react";

import { useReduxEntitiesFetch } from "@ds/hooks/use-redux-entities-fetch";
import { useRootDispatch, useRootSelector } from "@ds/hooks";

import { EntitySubtypeName, EntityTypeName } from "@ds/model/entity-model";

import { TableFilterOptions } from "@ds/modules/table-data/redux/types";

import { ActionsBar } from "@ds/components/actions-bar";
import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { IconType } from "@ds/components/icons";
import { InfoViewToggleRibbonButton } from "@ds/components/info-view/info-view-toggle-ribbon-button";
import { confirmWarning } from "@ds/components/popups/confirmation-dialog";
import { pluralizeDeleteMessage } from "@ds/utils/localization/common";

import { useDropzoneRef } from "../../hooks/use-dropzone-ref";
import { selectSelectedAssets } from "../../redux/selectors/common-selectors";
import { assetsActions } from "../../redux/slice";
import { getSupportedFormatsByEntityType } from "../../utils/helpers";
import { AssetUploadInput } from "../forms/common/assets-upload-input";
import { AssetsLayout } from "../types";

export const AssetsTableActionsMenu: FC<{
  filterOptions: TableFilterOptions;
  layout: AssetsLayout;
  totalSelected: number;
  onUpload: (files: File[]) => void;
  onUpdate: () => void;
  onSwitchLayout: (newLayout: AssetsLayout) => () => void;
}> = ({ filterOptions: { entitySubtype, tableType }, layout, totalSelected, onUpload, onUpdate, onSwitchLayout }) => {
  const selectedAssets = useRootSelector(state => selectSelectedAssets(state, tableType));

  const [dropzoneRef, openDropzone] = useDropzoneRef();

  const [toggleRefetch] = useReduxEntitiesFetch({ entityType: EntityTypeName.ASSET, entitySubtype }, { tableType });

  const dispatch = useRootDispatch();
  const onDeleteClickHandler = useCallback(
    () =>
      confirmWarning({
        message: pluralizeDeleteMessage("asset", selectedAssets.length),
        accept: () => dispatch(assetsActions.deleteAssets(selectedAssets.map(({ id }) => id))),
      }),
    [dispatch, selectedAssets],
  );

  const shouldShowUpdateButton = totalSelected === 1 && entitySubtype === EntitySubtypeName.ASSET_VIDEO;

  return (
    <ActionsBar>
      <Button ariaLabel="upload button" buttonType={ButtonType.Round} icon={IconType.Upload} onClick={openDropzone} />

      {shouldShowUpdateButton && (
        <Button
          ariaLabel="update button"
          icon={IconType.Update}
          buttonType={ButtonType.Round}
          isDisabled={!selectedAssets.length}
          onClick={onUpdate}
        />
      )}

      <Button
        ariaLabel="delete button"
        icon={IconType.Delete}
        buttonType={ButtonType.Round}
        actionType={ButtonActionType.Error}
        isDisabled={!selectedAssets.length}
        onClick={onDeleteClickHandler}
      />

      <Button
        ariaLabel="refresh button"
        buttonType={ButtonType.Round}
        icon={IconType.Refresh}
        onClick={toggleRefetch}
      />

      {layout === AssetsLayout.TILES && (
        <Button
          ariaLabel="list view button"
          buttonType={ButtonType.Round}
          icon={IconType.ListView}
          onClick={onSwitchLayout(AssetsLayout.LIST)}
        />
      )}

      {layout === AssetsLayout.LIST && (
        <Button
          ariaLabel="tiles view button"
          buttonType={ButtonType.Round}
          icon={IconType.TilesView}
          onClick={onSwitchLayout(AssetsLayout.TILES)}
        />
      )}

      <InfoViewToggleRibbonButton />
      <AssetUploadInput
        acceptMediaTypes={getSupportedFormatsByEntityType(entitySubtype)}
        dropzoneRef={dropzoneRef}
        onUpload={onUpload}
      />
    </ActionsBar>
  );
};
